import { useCallback, useMemo, useRef, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'sonner'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import tickAnimationUrl from '@/assets/tick mark animtion.lottie?url'
import {
  YesNoField,
  TextField,
  TextAreaField,
  NumberField,
} from '@/components/ChecklistFields'
import { TaskPhotos, type TaskPhotosHandle } from '@/components/TaskPhotos'
import {
  ChecklistItemPhotos,
  type ChecklistItemPhotosHandle,
} from '@/components/ChecklistItemPhotos'
import { TaskStatusBadge } from '@/components/TaskStatusBadge'
import { TaskTripInfo } from '@/components/TaskTripInfo'
import { Textarea } from '@/components/ui/textarea'
import { prntrTextareaClass } from '@/components/ui/prntr-input'
import {
  Ellipsis,
  Download,
  FilePenLine,
  Camera,
  NotebookText,
  Loader2,
} from '@/components/icons'
import { cn } from '@/lib/utils'
import {
  useTask,
  useTaskItemsUpdate,
  useTaskSubmit,
  useTaskRevertToDraft,
} from '@/lib/hooks/task'
import { useS3GetDownloadUrl } from '@/lib/hooks/s3'
import { useFieldNavigator } from '@/lib/hooks/useFieldNavigator'
import { exportTaskAsPdf } from '@/lib/task-pdf'

type ItemType = 'YesNo' | 'Text' | 'TextArea' | 'Number'

interface ChecklistItem {
  id: string
  label: string
  type: ItemType
  isRequired: boolean
  booleanResponse: boolean | null
  textResponse: string | null
  numberResponse: number | null
  note: string | null
  photoS3Keys: string[]
}

interface ItemResponse {
  booleanResponse: boolean | null
  textResponse: string | null
  numberResponse: number | null
  note: string | null
}

type ItemUpdate = {
  id: string
  booleanResponse?: boolean | null
  textResponse?: string | null
  numberResponse?: number | null
  note?: string | null
}

type FormValues = {
  responses: Record<string, ItemResponse>
}

function buildInitialValues(items: ChecklistItem[]): FormValues {
  const responses: Record<string, ItemResponse> = {}
  for (const item of items) {
    responses[item.id] = {
      booleanResponse: item.booleanResponse,
      textResponse: item.textResponse,
      numberResponse: item.numberResponse,
      note: item.note,
    }
  }
  return { responses }
}

function hasResponse(r: ItemResponse | undefined): boolean {
  if (!r) return false
  return (
    r.booleanResponse !== null ||
    (r.textResponse !== null && r.textResponse !== '') ||
    r.numberResponse !== null
  )
}

export const Route = createFileRoute('/_auth/task/$taskId')({
  component: CrewTaskRoute,
})

function CrewTaskRoute() {
  const { taskId } = Route.useParams()
  const router = useRouter()
  const isDesktop = useIsDesktop()
  const { data: task, isLoading } = useTask(taskId)
  const updateItems = useTaskItemsUpdate()
  const submitTask = useTaskSubmit()
  const revertToDraft = useTaskRevertToDraft()
  const getDownloadUrl = useS3GetDownloadUrl()

  const { containerRef: fieldsRef, navActive } = useFieldNavigator()

  const [isExporting, setIsExporting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{
    uploaded: number
    total: number
  } | null>(null)
  const [showSubmittedDialog, setShowSubmittedDialog] = useState(false)
  const [pendingPhotoCount, setPendingPhotoCount] = useState(0)
  const handlePendingPhotosChange = useCallback(setPendingPhotoCount, [])
  const photosRef = useRef<TaskPhotosHandle>(null)

  const itemPhotoRefs = useRef(new Map<string, ChecklistItemPhotosHandle>())
  const [itemPending, setItemPending] = useState<Record<string, number>>({})
  const handleItemPhotosPendingChange = useCallback((id: string, count: number) => {
    setItemPending((prev) => (prev[id] === count ? prev : { ...prev, [id]: count }))
  }, [])

  const initialValues = useMemo(
    () => (task ? buildInitialValues(task.items) : { responses: {} }),
    [task]
  )

  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    formState: { dirtyFields, isDirty, isSubmitting, isSubmitted },
  } = useForm<FormValues>({
    values: initialValues,
    mode: 'onChange',
  })

  const watchedResponses = watch('responses')

  // Confirm before the back button leaves a draft with unsaved edits.
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  if (isLoading || !task) {
    const loadingBody = (
      <>
        {/* Progress header */}
        <div
          className={cn(
            'sticky top-0 z-10 bg-surface-L0 py-2 flex items-center gap-2 border-b border-border-L2',
            isDesktop ? '-mx-8 px-8' : '-mx-4 px-4'
          )}
        >
          <Skeleton className="h-1 flex-1 rounded-full" />
          <Skeleton className="h-3 w-12" />
        </div>

        <div className="flex flex-col gap-4 pb-24 pt-4">
          {/* Trip info */}
          <Skeleton className="h-16 w-full rounded-2xl" />

          {/* Checklist items */}
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-3">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </>
    )

    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title="Task" />
          {loadingBody}
        </DesktopPage>
      )
    }

    return <AppLayout topBar={<TopBarBackButton title="Task" />}>{loadingBody}</AppLayout>
  }

  const responded = task.items.filter((i) => hasResponse(watchedResponses?.[i.id])).length
  const total = task.items.length
  const progress = total === 0 ? 0 : Math.round((responded / total) * 100)

  const requiredUnanswered = task.items.filter(
    (i) => i.isRequired && !hasResponse(watchedResponses?.[i.id])
  ).length

  const isDraft = task.status === 'Draft'
  const hasPendingPhotos = pendingPhotoCount > 0
  const hasPendingItemPhotos = Object.values(itemPending).some((c) => c > 0)
  const hasChanges = isDirty || hasPendingPhotos || hasPendingItemPhotos

  const handleBack = () => {
    if (isDraft && hasChanges) setShowDiscardDialog(true)
    else router.history.back()
  }

  const flushItemPhotos = async (onUploaded?: () => void): Promise<void> => {
    await Promise.all(
      task.items.map((i) => itemPhotoRefs.current.get(i.id)?.flushPending(onUploaded))
    )
  }

  const totalPendingUploads = (): number =>
    (photosRef.current?.pendingUploadCount() ?? 0) +
    task.items.reduce(
      (sum, i) => sum + (itemPhotoRefs.current.get(i.id)?.pendingUploadCount() ?? 0),
      0
    )

  const runPhotoUploads = async (): Promise<void> => {
    const total = totalPendingUploads()
    let uploaded = 0
    if (total > 0) setUploadProgress({ uploaded: 0, total })
    const onUploaded = () => {
      uploaded += 1
      setUploadProgress({ uploaded, total })
    }
    try {
      await photosRef.current?.flushPending(onUploaded)
      await flushItemPhotos(onUploaded)
    } finally {
      if (total > 0) setUploadProgress(null)
    }
  }

  const flushDirtyItems = async (values: FormValues): Promise<void> => {
    const dirty = dirtyFields.responses ?? {}
    const items = Object.keys(dirty).flatMap((id) => {
      const r = values.responses[id]
      if (!r) return []
      const df = (dirty[id] ?? {}) as Partial<Record<keyof ItemResponse, boolean>>
      const payload: ItemUpdate = { id }
      if (df.booleanResponse) payload.booleanResponse = r.booleanResponse
      if (df.textResponse) payload.textResponse = r.textResponse
      if (df.numberResponse) payload.numberResponse = r.numberResponse
      if (df.note) payload.note = r.note
      if (Object.keys(payload).length === 1) return []
      return [payload]
    })
    if (items.length === 0) return
    await updateItems.mutateAsync({ items })
  }

  const handleSaveDraft = async () => {
    setIsSavingDraft(true)
    try {
      const values = getValues()
      await runPhotoUploads()
      await flushDirtyItems(values)
      reset(values)
      toast.success('Draft saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save draft')
    } finally {
      setIsSavingDraft(false)
    }
  }

  const handleSubmitForReview = handleSubmit(async (values) => {
    if (requiredUnanswered > 0) {
      toast.error('Please complete all required fields')
      return
    }
    try {
      await runPhotoUploads()
      await flushDirtyItems(values)
      await submitTask.mutateAsync({ id: task.id })
      setShowSubmittedDialog(true)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit task')
    }
  })

  const handleRevertToDraft = async () => {
    try {
      await revertToDraft.mutateAsync({ id: task.id })
      toast.success('Task reverted to draft')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revert task')
    }
  }

  const handleExportPdf = async () => {
    if (!task) return
    setIsExporting(true)
    try {
      await exportTaskAsPdf({
        task: {
          name: task.name,
          status: task.status,
          items: task.items,
          photoS3Keys: task.photoS3Keys,
          submittedBy: task.submittedBy,
          submittedAt: task.submittedAt,
          tripLeg: task.tripLeg,
        },
        getDownloadUrl: async (s3Key) => {
          const { downloadUrl } = await getDownloadUrl.mutateAsync({ s3Key })
          return downloadUrl
        },
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to export PDF')
    } finally {
      setIsExporting(false)
    }
  }

  const savingDraft = isSavingDraft
  const submitting = isSubmitting || submitTask.isPending
  const isBusy = savingDraft || submitting || revertToDraft.isPending
  const uploadLabel =
    uploadProgress && uploadProgress.total > 0
      ? `Uploading ${uploadProgress.uploaded}/${uploadProgress.total}…`
      : null

  const statusAndMenu = (
    <div className="flex items-center gap-2">
      {isDesktop && hasChanges && (
        <span className="text-content-brand-bold font-bold">*</span>
      )}
      <TaskStatusBadge status={task.status} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="size-8 rounded-full bg-surface-L2 flex items-center justify-center hover:bg-surface-L2/80 transition-colors"
            aria-label="Task menu"
          >
            <Ellipsis className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isDraft && (
            <DropdownMenuItem
              onClick={handleRevertToDraft}
              disabled={revertToDraft.isPending}
            >
              <FilePenLine className="size-4" />
              {revertToDraft.isPending ? 'Reverting...' : 'Draft'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleExportPdf} disabled={isExporting}>
            <Download className="size-4" />
            {isExporting ? 'Exporting...' : 'Export as PDF'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )

  const body = (
    <>
      <div
        className={cn(
          'sticky top-0 z-10 bg-surface-L0 py-2 flex items-center gap-2 border-b border-border-L2',
          isDesktop ? '-mx-8 px-8' : '-mx-4 px-4'
        )}
      >
        <Progress
          value={progress}
          className="h-1 flex-1 bg-background-brand-bold/20"
          indicatorClassName="bg-background-brand-bold"
        />
        <span className="text-xs font-semibold text-content-primary-subtle">
          {responded} of {total}
        </span>
      </div>

      {/* The field navigator walks this container's inputs in DOM order;
          Yes/No rows render buttons (no input), so prev/next skip them. */}
      <div
        ref={fieldsRef}
        className={cn('flex flex-col gap-4 pt-4', !isDesktop && 'pb-24')}
      >
        {task.tripLeg && <TaskTripInfo tripLeg={task.tripLeg} />}

        {task.items.length === 0 ? (
          <p className="text-sm text-content-primary-subtle py-8 text-center">
            This task has no items.
          </p>
        ) : (
          task.items.map((item) => {
            const isMissingRequired =
              isDraft &&
              item.isRequired &&
              !hasResponse(watchedResponses?.[item.id])
            return (
              <div key={item.id} data-field-id={item.id}>
                <FormField
                  item={item}
                  taskId={task.id}
                  control={control}
                  readOnly={!isDraft}
                  error={isMissingRequired && isSubmitted ? 'Required' : undefined}
                  registerPhotoRef={(h) => {
                    if (h) itemPhotoRefs.current.set(item.id, h)
                    else itemPhotoRefs.current.delete(item.id)
                  }}
                  onPhotosPendingChange={handleItemPhotosPendingChange}
                />
              </div>
            )
          })
        )}

        <TaskPhotos
          ref={photosRef}
          taskId={task.id}
          photoS3Keys={task.photoS3Keys}
          readOnly={!isDraft}
          onPendingChange={handlePendingPhotosChange}
        />
      </div>

      {/* While typing, the native keyboard accessory bar handles field
          navigation; hide the action bar so the two don't overlap. */}
      {isDraft && !navActive && (
        <div
          className={
            isDesktop
              ? undefined
              : 'fixed bottom-0 left-0 right-0 bg-surface-L0 border-t border-border-L2 p-4 safe-bottom'
          }
        >
          <div className={cn('flex gap-2', isDesktop && 'justify-end')}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={cn(!isDesktop && 'flex-1')}
              onClick={handleSaveDraft}
              disabled={!hasChanges || isBusy}
            >
              {savingDraft ? uploadLabel ?? 'Saving Draft...' : 'Save Draft'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className={cn(!isDesktop && 'flex-1')}
              onClick={handleSubmitForReview}
              disabled={!isDraft || isBusy}
            >
              {submitting ? uploadLabel ?? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent className="bg-surface-L0 rounded-lg border border-border-L2 shadow-lg p-8 max-w-[320px] gap-2">
          <div className="flex flex-col items-center gap-4">
            <p className="text-lg font-bold text-content-primary-bold text-center tracking-[-1px] w-full leading-7">
              Discard Changes?
            </p>
            <p className="text-sm text-content-primary-subtle text-center w-full leading-5">
              You have unsaved changes. If you leave now, they'll be lost.
            </p>
            <div className="flex w-full gap-2 pt-2">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowDiscardDialog(false)}
              >
                Keep Editing
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  setShowDiscardDialog(false)
                  router.history.back()
                }}
              >
                Discard
              </Button>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showSubmittedDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowSubmittedDialog(false)
            router.history.back()
          }
        }}
      >
        <DialogContent className="bg-surface-L0 rounded-lg border border-border-L2 shadow-lg p-8 max-w-[320px] gap-2">
          <div className="flex flex-col items-center gap-4">
            <DotLottieReact
              src={tickAnimationUrl}
              autoplay
              loop={false}
              style={{ width: 44, height: 44 }}
            />
            <p className="text-lg font-bold text-content-primary-bold text-center tracking-[-1px] w-full leading-7">
              Checklist Submitted
            </p>
            <p className="text-sm text-content-primary-subtle text-center w-full leading-5">
              Your checklist has been successfully submitted.
            </p>
            <div className="flex w-full gap-2 pt-2">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={handleExportPdf}
                disabled={isExporting}
              >
                Export PDF
              </Button>
              <Button
                onClick={() => {
                  setShowSubmittedDialog(false)
                  router.history.back()
                }}
                variant="primary"
                className="flex-1"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isExporting}>
        <DialogContent
          className="bg-surface-L0 rounded-lg border border-border-L2 shadow-lg p-8 max-w-[320px] gap-2 [&>button]:hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-content-primary-subtle" />
            <p className="text-lg font-bold text-content-primary-bold text-center tracking-[-1px] w-full leading-7">
              Exporting PDF
            </p>
            <p className="text-sm text-content-primary-subtle text-center w-full leading-5">
              Preparing your checklist and photos. This may take a moment.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )

  if (isDesktop) {
    // The breadcrumb goes through handleBack (not a plain Link) so the
    // unsaved-changes discard guard still fires.
    const breadcrumb = (
      <span>
        <button
          type="button"
          onClick={handleBack}
          disabled={submitting}
          className="hover:underline"
        >
          Checklists
        </button>
        <span className="text-content-primary-subtle"> / {task.name}</span>
      </span>
    )

    return (
      <DesktopPage>
        <DesktopPageHeader
          title={task.name}
          breadcrumb={breadcrumb}
          action={statusAndMenu}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackButton
          title={
            <>
              {task.name}
              {hasChanges && (
                <span className="text-content-brand-bold">*</span>
              )}
            </>
          }
          disableBack={submitting}
          onBack={handleBack}
          rightElement={statusAndMenu}
        />
      }
    >
      {body}
    </AppLayout>
  )
}

function FormField({
  item,
  taskId,
  control,
  readOnly,
  error,
  registerPhotoRef,
  onPhotosPendingChange,
}: {
  item: ChecklistItem
  taskId: string
  control: ReturnType<typeof useForm<FormValues>>['control']
  readOnly: boolean
  error?: string
  registerPhotoRef: (handle: ChecklistItemPhotosHandle | null) => void
  onPhotosPendingChange: (itemId: string, count: number) => void
}) {
  const common = {
    label: item.label,
    isRequired: item.isRequired,
    readOnly,
    error,
    footer: (
      <Controller
        control={control}
        name={`responses.${item.id}.note`}
        render={({ field }) => (
          <ChecklistItemFooter
            taskId={taskId}
            itemId={item.id}
            photoS3Keys={item.photoS3Keys}
            readOnly={readOnly}
            allowNote={item.type === 'YesNo'}
            registerPhotoRef={registerPhotoRef}
            onPhotosPendingChange={onPhotosPendingChange}
            note={field.value}
            onNoteChange={field.onChange}
          />
        )}
      />
    ),
  }

  if (item.type === 'YesNo') {
    return (
      <Controller
        control={control}
        name={`responses.${item.id}.booleanResponse`}
        render={({ field }) => (
          <YesNoField {...common} value={field.value} onCommit={field.onChange} />
        )}
      />
    )
  }

  if (item.type === 'TextArea') {
    return (
      <Controller
        control={control}
        name={`responses.${item.id}.textResponse`}
        render={({ field }) => (
          <TextAreaField {...common} value={field.value} onCommit={field.onChange} />
        )}
      />
    )
  }

  if (item.type === 'Number') {
    return (
      <Controller
        control={control}
        name={`responses.${item.id}.numberResponse`}
        render={({ field }) => (
          <NumberField {...common} value={field.value} onCommit={field.onChange} />
        )}
      />
    )
  }

  return (
    <Controller
      control={control}
      name={`responses.${item.id}.textResponse`}
      render={({ field }) => (
        <TextField {...common} value={field.value} onCommit={field.onChange} />
      )}
    />
  )
}

function ChecklistItemFooter({
  taskId,
  itemId,
  photoS3Keys,
  readOnly,
  allowNote,
  registerPhotoRef,
  onPhotosPendingChange,
  note,
  onNoteChange,
}: {
  taskId: string
  itemId: string
  photoS3Keys: string[]
  readOnly: boolean
  allowNote: boolean
  registerPhotoRef: (handle: ChecklistItemPhotosHandle | null) => void
  onPhotosPendingChange: (itemId: string, count: number) => void
  note: string | null
  onNoteChange: (value: string | null) => void
}) {
  const photosRef = useRef<ChecklistItemPhotosHandle | null>(null)
  const [noteEditing, setNoteEditing] = useState(false)

  const setPhotosRef = (handle: ChecklistItemPhotosHandle | null) => {
    photosRef.current = handle
    registerPhotoRef(handle)
  }

  const hasNote = allowNote && (note ?? '').trim().length > 0
  const showNoteInput = allowNote && !readOnly && noteEditing
  const showNoteReadOnly = hasNote && !showNoteInput

  return (
    <div className="flex flex-col gap-2">
      {showNoteReadOnly && (
        <button
          type="button"
          disabled={readOnly}
          onClick={() => setNoteEditing(true)}
          className="text-left rounded-lg bg-surface-L2 px-3 py-2 text-sm text-content-primary-subtle whitespace-pre-wrap"
        >
          {note}
        </button>
      )}

      {showNoteInput && (
        <div className="flex flex-col gap-2 border-t border-border-L2 pt-4">
          <p className="text-xs font-semibold text-content-primary-subtle">Notes</p>
          <Textarea
            autoFocus
            value={note ?? ''}
            rows={3}
            placeholder="Add a note"
            onChange={(e) => {
              const next = e.target.value
              onNoteChange(next.length ? next : null)
            }}
            className={prntrTextareaClass}
          />
        </div>
      )}

      <ChecklistItemPhotos
        ref={setPhotosRef}
        taskId={taskId}
        itemId={itemId}
        photoS3Keys={photoS3Keys}
        readOnly={readOnly}
        onPendingChange={(count) => onPhotosPendingChange(itemId, count)}
      />

      {!readOnly && (
        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            roundness="round"
            aria-label="Add photo"
            onClick={() => photosRef.current?.openPicker()}
            className="w-8 px-0 text-content-brand-bold"
          >
            <Camera />
          </Button>
          {allowNote && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              roundness="round"
              aria-label={noteEditing ? 'Close note' : hasNote ? 'Edit note' : 'Add note'}
              onClick={() => setNoteEditing((v) => !v)}
              className={cn(
                'w-8 px-0 text-content-brand-bold',
                (noteEditing || hasNote) && 'bg-background-brand-subtle'
              )}
            >
              <NotebookText />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
